// store/features/templates/templatesSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AITemplate, EventStructure } from './templatesAPI';

interface TemplatesState {
  // Selected template for generation
  selectedTemplate: AITemplate | null;
  selectedTemplateId: string | null;
  
  // Template customization form data
  customizations: Partial<EventStructure>;
  
  // Generated event preview
  generatedEvent: EventStructure | null;
  generatedEventId: string | null;
  
  // UI state
  isGenerating: boolean;
  generationProgress: number;
  generationError: string | null;
  
  // Filters
  filters: {
    industry: string | null;
    template_type: 'event' | 'email' | null;
    search: string;
  };
  
  // Preview mode
  showPreview: boolean;
  previewData: EventStructure | null;
}

const initialState: TemplatesState = {
  selectedTemplate: null,
  selectedTemplateId: null,
  customizations: {},
  generatedEvent: null,
  generatedEventId: null,
  isGenerating: false,
  generationProgress: 0,
  generationError: null,
  filters: {
    industry: null,
    template_type: null,
    search: '',
  },
  showPreview: false,
  previewData: null,
};

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    // ✅ Template Selection
    setSelectedTemplate: (state, action: PayloadAction<AITemplate | null>) => {
      state.selectedTemplate = action.payload;
      state.selectedTemplateId = action.payload?.documentId || null;
      // Pre-fill customizations with template structure
      if (action.payload) {
        state.customizations = { ...action.payload.structure };
      }
    },

    // ✅ Update Customizations
    setCustomizations: (state, action: PayloadAction<Partial<EventStructure>>) => {
      state.customizations = {
        ...state.customizations,
        ...action.payload,
      };
    },

    resetCustomizations: (state) => {
      state.customizations = state.selectedTemplate?.structure || {};
    },

    // ✅ Generation State
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
      if (!action.payload) {
        state.generationProgress = 0;
      }
    },

    setGenerationProgress: (state, action: PayloadAction<number>) => {
      state.generationProgress = action.payload;
    },

    setGenerationError: (state, action: PayloadAction<string | null>) => {
      state.generationError = action.payload;
    },

    // ✅ Generated Event
    setGeneratedEvent: (state, action: PayloadAction<EventStructure>) => {
      state.generatedEvent = action.payload;
      state.previewData = action.payload;
      state.showPreview = true;
    },

    setGeneratedEventId: (state, action: PayloadAction<string>) => {
      state.generatedEventId = action.payload;
    },

    clearGeneratedEvent: (state) => {
      state.generatedEvent = null;
      state.generatedEventId = null;
      state.showPreview = false;
      state.previewData = null;
    },

    // ✅ Filters
    setIndustryFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.industry = action.payload;
    },

    setTemplateTypeFilter: (state, action: PayloadAction<'event' | 'email' | null>) => {
      state.filters.template_type = action.payload;
    },

    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },

    resetFilters: (state) => {
      state.filters = {
        industry: null,
        template_type: null,
        search: '',
      };
    },

    // ✅ Preview Control
    setShowPreview: (state, action: PayloadAction<boolean>) => {
      state.showPreview = action.payload;
    },

    setPreviewData: (state, action: PayloadAction<EventStructure | null>) => {
      state.previewData = action.payload;
    },

    // ✅ Reset All
    resetTemplatesState: () => initialState,
  },
});

export const {
  setSelectedTemplate,
  setCustomizations,
  resetCustomizations,
  setIsGenerating,
  setGenerationProgress,
  setGenerationError,
  setGeneratedEvent,
  setGeneratedEventId,
  clearGeneratedEvent,
  setIndustryFilter,
  setTemplateTypeFilter,
  setSearchFilter,
  resetFilters,
  setShowPreview,
  setPreviewData,
  resetTemplatesState,
} = templatesSlice.actions;

export default templatesSlice.reducer;
